import { useState } from "react";
import { LegalLayout, LegalSection } from "../components/LegalLayout";
import { analyticsAllowed, setAnalyticsAllowed } from "../lib/analyticsConsent";

export default function Privacy() {
  const [allowed, setAllowed] = useState(() => analyticsAllowed());
  const toggle = () => {
    const next = !allowed;
    setAllowed(next);
    setAnalyticsAllowed(next);
  };
  return (
    <LegalLayout title="Privacy Policy" updated="June 2026" testid="privacy-page">
      <p>
        This Privacy Policy explains how RoadRnr ("RoadRnr", "we", "us") handles
        information when you use our automotive event discovery app and website
        (the "Service"). If you have any questions, contact us at{" "}
        <a href="mailto:roadrnrapp@gmail.com" className="font-semibold text-[#ff2a00]">
          roadrnrapp@gmail.com
        </a>
        .
      </p>

      <LegalSection heading="Information we collect">
        <p>We only collect information needed to run the Service:</p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>
            <span className="font-semibold text-white/90">Account &amp; profile.</span>{" "}
            When you create an account we collect your email address and password
            (handled by our authentication provider), and any profile details you
            add such as display name, profile image, and — for organisers — an
            organiser name and bio. RoadRnr supports email/password, Google sign-in
            and Sign in with Apple.
          </li>
          <li>
            <span className="font-semibold text-white/90">Notifications.</span> We may
            store device and push tokens so we can send you notifications. These are
            delivered using Apple, Google and/or Expo push delivery services where
            applicable. You can control notification permissions in your device
            settings at any time.
          </li>
          <li>
            <span className="font-semibold text-white/90">Event information &amp; images.</span>{" "}
            If you submit or list an event, we collect the details you provide,
            including name, description, category, dates and times, location and
            address, cover image(s), and any website, ticket or social links and
            contact email you choose to include.
          </li>
          <li>
            <span className="font-semibold text-white/90">Saved events &amp; activity.</span>{" "}
            Events you save are stored on your device. We also keep records of
            events you create and, where applicable, your listing usage.
          </li>
          <li>
            <span className="font-semibold text-white/90">Location.</span> With your
            permission, we use your device location to show events near you and to
            display distances. Your precise location is used within your browser for
            these features; your last known location may be stored locally on your
            device to speed this up. You can turn location off at any time in your
            device or browser settings.
          </li>
          <li>
            <span className="font-semibold text-white/90">Payments.</span> Digital
            purchases made in the iOS app (such as listings and Boost) are processed
            through Apple In-App Purchase / StoreKit. Apple handles the payment
            transaction, and RoadRnr receives only the purchase information needed to
            verify and fulfil the purchase — not your full Apple payment-card details.
            Android digital purchases may use Google Play Billing where applicable.
            Stripe may be used for supported website or other non-app-store payment
            flows, but not for iOS in-app digital purchases. In all cases we do not
            receive or store your full card details; we keep a record of the
            transaction (such as a payment reference and what it was for).
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="How we use information">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>To create and manage your account and profile.</li>
          <li>To display events and let you discover, search, save and share them.</li>
          <li>To show events near you and calculate distances (with your permission).</li>
          <li>To publish organiser-submitted listings and review them for quality.</li>
          <li>To process payments for paid listings and Boost.</li>
          <li>To show advertising and to protect the Service against abuse and spam.</li>
          <li>To send service messages such as sign-in confirmations and password resets.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="Service providers">
        <p>
          We rely on trusted third parties to provide the Service. Your information
          may be processed by them for the purposes above:
        </p>
        <ul className="list-disc space-y-1.5 pl-5">
          <li>
            <span className="font-semibold text-white/90">Supabase</span> — account
            authentication, and hosting of our database and image storage (profiles,
            events and uploaded images).
          </li>
          <li>
            <span className="font-semibold text-white/90">Apple</span> — App Store
            In-App Purchase / StoreKit for iOS digital purchases, Sign in with Apple,
            and push notification delivery.
          </li>
          <li>
            <span className="font-semibold text-white/90">Stripe</span> — secure
            payment processing for supported website or other non-app-store payment
            flows (not iOS in-app digital purchases).
          </li>
          <li>
            <span className="font-semibold text-white/90">Google</span> — Google sign-in,
            Google Play Billing for Android digital purchases where applicable, and
            advertising through Google AdSense, which may use cookies to serve and
            measure ads.
          </li>
          <li>
            <span className="font-semibold text-white/90">Expo</span> — push
            notification delivery where applicable.
          </li>
          <li>
            <span className="font-semibold text-white/90">Maps &amp; geocoding</span> —
            map tiles and location look-ups (for example converting a place name or
            postcode into map coordinates) are provided using OpenStreetMap-based
            services and CARTO map tiles.
          </li>
          <li>
            <span className="font-semibold text-white/90">Bot protection</span> — we
            use Cloudflare Turnstile to help protect sign-up and sign-in from abuse.
          </li>
        </ul>
        <p>
          These providers process information under their own terms and privacy
          policies.
        </p>
      </LegalSection>

      <LegalSection heading="Cookies &amp; local storage">
        <p>
          We use your browser's local storage for essentials such as keeping you
          signed in, remembering your saved events and theme preference, and caching
          your last known location for faster event discovery. Google AdSense may set
          cookies to deliver and measure advertising.
        </p>
      </LegalSection>

      <LegalSection heading="Your choices &amp; rights">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>You can view and update your profile details in the app.</li>
          <li>You can turn location access on or off in your device/browser settings.</li>
          <li>You can control notification permissions in your device settings.</li>
          <li>
            You can request account deletion from your RoadRnr profile. When you do,
            your account and associated user content are deleted, except records that
            must be retained for legitimate legal, accounting, transaction,
            fraud-prevention or dispute purposes. Where applicable, RoadRnr revokes the
            associated Sign in with Apple authorization when the account is deleted.
          </li>
          <li>
            You can also ask us to access, correct or delete your account information
            by emailing{" "}
            <a href="mailto:roadrnrapp@gmail.com" className="font-semibold text-[#ff2a00]">
              roadrnrapp@gmail.com
            </a>
            .
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="Analytics preference">
        <p>
          We use analytics and measurement tools to understand how RoadRnr is used
          and to monitor usage, performance and errors so we can improve it. You can
          opt out of analytics at any time here. This does not affect essential
          storage such as keeping you signed in.
        </p>
        <p className="mt-3">
          We do not sell your personal data. Where Apple requires permission to track
          you across apps or websites owned by other companies, we will request your
          permission first.
        </p>
        <div className="mt-3 flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="pr-4">
            <p className="text-sm font-semibold text-white/90">Allow analytics</p>
            <p className="mt-0.5 text-xs text-white/50">
              {allowed ? "Analytics is on" : "Analytics is off"}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={allowed}
            data-testid="analytics-preference-toggle"
            onClick={toggle}
            className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
              allowed ? "bg-[#ff2a00]" : "bg-white/20"
            }`}
          >
            <span
              className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-transform ${
                allowed ? "translate-x-[22px]" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>
      </LegalSection>

      <LegalSection heading="Data retention &amp; security">
        <p>
          We keep information for as long as your account is active or as needed to
          provide the Service, and then delete or anonymise it unless we need to keep
          it to meet a legal obligation. We take reasonable technical and
          organisational measures to protect information, and our providers host data
          on secure infrastructure. No method of storage or transmission is completely
          secure, so we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection heading="Children">
        <p>
          RoadRnr is intended for a general audience and is not directed at children.
        </p>
      </LegalSection>

      <LegalSection heading="Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we will
          update the date at the top of this page.
        </p>
      </LegalSection>

      <LegalSection heading="Contact">
        <p>
          Questions about this policy or your information? Email us at{" "}
          <a href="mailto:roadrnrapp@gmail.com" className="font-semibold text-[#ff2a00]">
            roadrnrapp@gmail.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
